/**
 * Base-path helpers for deploying the SPA under a subpath (e.g. GitHub project
 * Pages serves at `/os/`).
 *
 * WHY: the router and links work in an internal, base-relative path space
 * (`/ideas`, `/ideas/:id`). These pure functions translate to/from the on-page
 * path space by adding/stripping the deploy base, so the rest of the app never
 * hard-codes a deployment location. Base `''` (root) is a no-op.
 */

/** Normalize a raw base (e.g. Vite's `BASE_URL`) to `''` or `/seg` (no trailing slash). */
export function normalizeBase(rawBase: string | undefined): string {
  if (!rawBase || rawBase === '/') return '';
  const trimmed = rawBase.endsWith('/') ? rawBase.slice(0, -1) : rawBase;
  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
}

/** Build an on-page path: `joinBase('/os', '/ideas')` → `/os/ideas`. */
export function joinBase(base: string, path: string): string {
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${base}${p}`;
}

/**
 * Strip the base from an on-page pathname to the internal path.
 * `stripBase('/os', '/os/ideas')` → `/ideas`; the bare base → `/`.
 */
export function stripBase(base: string, pathname: string): string {
  if (!base) return pathname || '/';
  if (pathname === base) return '/';
  if (pathname.startsWith(`${base}/`)) return pathname.slice(base.length) || '/';
  return pathname; // outside our base; let the router show not-found/empty
}
